import { useState } from "react";
import { ArrowRight, Mail, MessageCircle, Phone } from "lucide-react";
import {
  trackContactFormStart,
  trackContactFormSubmit,
  trackEmailClick,
  trackPhoneClick,
  trackWhatsappClick,
} from "@/lib/analytics";
import { contactNeedTypes } from "@/lib/commercial-content";
import { EMAIL, WHATSAPP_DISPLAY, buildWhatsappLink } from "@/lib/constants";

const initialForm = {
  company: "",
  name: "",
  contact: "",
  need: contactNeedTypes[0] || "",
  problem: "",
};

const ContactPage = () => {
  const [form, setForm] = useState(initialForm);
  const [started, setStarted] = useState(false);

  const updateField = (field) => (event) => {
    if (!started) {
      trackContactFormStart("contact_page");
      setStarted(true);
    }
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const message = [
    "Olá! Quero falar com a BezierClick.",
    `Empresa: ${form.company || "-"}`,
    `Nome: ${form.name || "-"}`,
    `Contacto: ${form.contact || "-"}`,
    `Necessidade: ${form.need || "-"}`,
    `Problema: ${form.problem || "-"}`,
  ].join("\n");
  const whatsappHref = buildWhatsappLink(message, "contact_form");

  const handleSubmit = (event) => {
    event.preventDefault();
    trackContactFormSubmit("contact_page");
    trackWhatsappClick("contact_form");
    window.open(whatsappHref, "_blank", "noopener,noreferrer");
  };

  return (
    <main id="main-content" className="bg-[#0a0a0a] pt-24">
      <section className="bg-[#ff6b35] noise py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.9fr)_minmax(320px,1.1fr)] gap-8 lg:gap-12 items-start">
            <div>
              <div className="inline-block bg-[#0a0a0a] text-white px-3 py-1 brutal-border font-mono text-xs uppercase tracking-widest mb-5">
                Contacto
              </div>
              <h1 className="font-display text-[#0a0a0a] text-[2.65rem] min-[380px]:text-5xl sm:text-7xl lg:text-8xl uppercase leading-[0.9]">
                Conte-nos o problema
              </h1>
              <p className="mt-6 max-w-2xl text-[#0a0a0a]/80 text-lg sm:text-xl leading-snug">
                Não precisa saber qual tecnologia utilizar. Explique-nos o problema e
                ajudamos a identificar a solução adequada.
              </p>
              <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <a href={buildWhatsappLink()} target="_blank" rel="noreferrer" onClick={() => trackWhatsappClick("contact_whatsapp")} className="bg-white brutal-border px-4 py-3 font-mono text-xs uppercase tracking-widest text-[#0a0a0a] inline-flex items-center gap-2">
                  <MessageCircle size={16} className="shrink-0" />
                  WhatsApp
                </a>
                <a href="tel:+351934400491" onClick={() => trackPhoneClick("contact_phone")} className="bg-white brutal-border px-4 py-3 font-mono text-xs uppercase tracking-widest text-[#0a0a0a] inline-flex items-center gap-2">
                  <Phone size={16} className="shrink-0" />
                  Telefone
                </a>
                <a href={`mailto:${EMAIL}`} onClick={() => trackEmailClick("contact_email")} className="bg-white brutal-border px-4 py-3 font-mono text-xs uppercase tracking-widest text-[#0a0a0a] inline-flex items-center gap-2">
                  <Mail size={16} className="shrink-0" />
                  Email
                </a>
              </div>
              <p className="mt-4 font-mono text-[0.68rem] uppercase tracking-widest text-[#0a0a0a]/70">
                {WHATSAPP_DISPLAY} · {EMAIL}
              </p>
            </div>

            <form className="brutal-card bg-white p-5 sm:p-7 space-y-5" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <label className="block">
                  <span className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">
                    Empresa
                  </span>
                  <input
                    type="text"
                    value={form.company}
                    onChange={updateField("company")}
                    className="mt-2 w-full bg-white brutal-border px-4 py-3 text-[#0a0a0a] outline-none focus:ring-4 focus:ring-[#ff6b35]"
                    autoComplete="organization"
                  />
                </label>

                <label className="block">
                  <span className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">
                    Nome
                  </span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={updateField("name")}
                    className="mt-2 w-full bg-white brutal-border px-4 py-3 text-[#0a0a0a] outline-none focus:ring-4 focus:ring-[#ff6b35]"
                    autoComplete="name"
                  />
                </label>
              </div>

              <label className="block">
                <span className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">
                  Contacto
                </span>
                <input
                  type="text"
                  value={form.contact}
                  onChange={updateField("contact")}
                  className="mt-2 w-full bg-white brutal-border px-4 py-3 text-[#0a0a0a] outline-none focus:ring-4 focus:ring-[#ff6b35]"
                  autoComplete="on"
                />
              </label>

              <label className="block">
                <span className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">
                  Necessidade
                </span>
                <select
                  value={form.need}
                  onChange={updateField("need")}
                  className="mt-2 w-full bg-white brutal-border px-4 py-3 text-[#0a0a0a] outline-none focus:ring-4 focus:ring-[#ff6b35]"
                >
                  {contactNeedTypes.map((need) => (
                    <option key={need} value={need}>
                      {need}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="font-mono text-xs uppercase tracking-widest text-[#0a0a0a]">
                  Problema
                </span>
                <textarea
                  value={form.problem}
                  onChange={updateField("problem")}
                  rows={6}
                  maxLength={900}
                  className="mt-2 w-full resize-y bg-white brutal-border px-4 py-3 text-[#0a0a0a] outline-none focus:ring-4 focus:ring-[#ff6b35]"
                />
              </label>

              <button
                type="submit"
                className="btn-brutal btn-brutal-orange w-full justify-center text-center"
              >
                <span>Enviar pelo WhatsApp</span>
                <ArrowRight size={18} className="shrink-0" />
              </button>
              <p className="font-mono text-[0.68rem] uppercase tracking-widest text-[#0a0a0a]/60 leading-relaxed">
                Não enviamos dados pessoais para Analytics. O formulário apenas prepara a
                mensagem para abrir a conversa no WhatsApp.
              </p>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
